/**
 * Google Sheets Sync Logs Component
 * Manages Google Sheets sync logs with proper API integration
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNotifications } from '../common/NotificationSystem';
import { googleSheetsService } from '../../services/googleSheetsService';
import ProfessionalLoader from "../common/ProfessionalLoader";
import './GoogleSheetsSyncLogs.css';

const GoogleSheetsSyncLogs = () => {
  const { showError } = useNotifications();
  
  // State management
  const [logs, setLogs] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedLogs, setSelectedLogs] = useState([]);
  
  // Filters
  const [filters, setFilters] = useState({
    sync_type: '',
    status: '',
    date_from: '',
    date_to: '',
    ordering: '-started_at'
  });
  
  // Statistics
  const [stats, setStats] = useState({
    total_logs: 0,
    completed_logs: 0,
    failed_logs: 0,
    in_progress_logs: 0,
    pending_logs: 0
  });

  // Load logs
  const loadLogs = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await googleSheetsService.getBackupSyncLogs(filters);
      
      if (response.success) {
        const logsData = response.data || [];
        const formattedLogs = logsData.map(log => googleSheetsService.formatSyncLog(log));
        setLogs(formattedLogs);
        updateStatistics(formattedLogs);
      } else {
        showError(response.message || 'Failed to load sync logs', 'Error');
      }
    } catch (error) {
      showError('Failed to load sync logs', 'Error');
    } finally {
      setIsLoading(false);
    }
  }, [filters, showError]);

  // Update statistics
  const updateStatistics = (logsData) => {
    const stats = googleSheetsService.getSyncStatistics(logsData);
    setStats({
      total: stats.total || 0,
      completed: stats.completed || 0,
      failed: stats.failed || 0,
      inProgress: stats.inProgress || 0,
      pending: stats.pending || 0
    });
  };

  // Load logs on component mount
  useEffect(() => {
    loadLogs();
  }, [loadLogs]);

  // Handle log selection
  const handleLogSelect = (logId, isSelected) => {
    setSelectedLogs(prev => 
      isSelected 
        ? [...prev, logId]
        : prev.filter(id => id !== logId)
    );
  };

  // Handle select all
  const handleSelectAll = (isSelected) => {
    if (isSelected) {
      setSelectedLogs(logs.map(log => log.id));
    } else {
      setSelectedLogs([]);
    }
  };

  // Handle filter changes
  const handleFilterChange = (newFilters) => {
    setFilters(prev => ({
      ...prev,
      ...newFilters
    }));
  };

  if (isLoading) {
    return (
      <div className="google-sheets-sync-logs">
        <div className="loading-container">
          <ProfessionalLoader size="large" variant="default" />
        </div>
      </div>
    );
  }

  return (
    <div className="google-sheets-sync-logs">
      {/* Header */}
      <div className="logs-header">
        <div className="header-content">
          <h2>📋 Sync Logs</h2>
          <p>Monitor Google Sheets synchronization activities</p>
        </div>
        <div className="header-actions">
          <div className="logs-summary">
            <span className="summary-text">
              {logs.length} sync log{logs.length !== 1 ? 's' : ''} found
            </span>
          </div>
        </div>
      </div>

      {/* Statistics */}
      <div className="logs-stats">
        <div className="stat-card">
          <div className="stat-icon">📊</div>
          <div className="stat-content">
            <h3>Total Logs</h3>
            <p className="stat-value">{stats.total}</p>
          </div>
        </div>
        <div className="stat-card completed">
          <div className="stat-icon">✅</div>
          <div className="stat-content">
            <h3>Completed</h3>
            <p className="stat-value">{stats.completed}</p>
          </div>
        </div>
        <div className="stat-card failed">
          <div className="stat-icon">❌</div>
          <div className="stat-content">
            <h3>Failed</h3>
            <p className="stat-value">{stats.failed}</p>
          </div>
        </div>
        <div className="stat-card in-progress">
          <div className="stat-icon">🔄</div>
          <div className="stat-content">
            <h3>In Progress</h3>
            <p className="stat-value">{stats.inProgress}</p>
          </div>
        </div>
        <div className="stat-card pending">
          <div className="stat-icon">⏳</div>
          <div className="stat-content">
            <h3>Pending</h3>
            <p className="stat-value">{stats.pending}</p>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="logs-filters">
        <div className="filters-header">
          <h3>🔍 Filters</h3>
          <button
            className="btn btn-outline btn-sm"
            onClick={() => handleFilterChange({
              sync_type: '',
              status: '',
              date_from: '',
              date_to: '',
              ordering: '-started_at'
            })}
          >
            Clear All
          </button>
        </div>
        
        <div className="filters-grid">
          <div className="filter-row">
            <div className="filter-group">
              <label className="filter-label">Sync Type</label>
              <select
                className="filter-select"
                value={filters.sync_type}
                onChange={(e) => handleFilterChange({ sync_type: e.target.value })}
              >
                <option value="">All Types</option>
                <option value="manual">Manual</option>
                <option value="automated">Automated</option>
                <option value="scheduled">Scheduled</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label className="filter-label">Status</label>
              <select
                className="filter-select"
                value={filters.status}
                onChange={(e) => handleFilterChange({ status: e.target.value })}
              >
                <option value="">All Statuses</option>
                <option value="completed">Completed</option>
                <option value="failed">Failed</option>
                <option value="in_progress">In Progress</option>
                <option value="pending">Pending</option>
                <option value="cancelled">Cancelled</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label className="filter-label">Sort By</label>
              <select
                className="filter-select"
                value={filters.ordering}
                onChange={(e) => handleFilterChange({ ordering: e.target.value })}
              >
                <option value="-started_at">Started (Newest)</option>
                <option value="started_at">Started (Oldest)</option>
                <option value="status">Status</option>
                <option value="sync_type">Sync Type</option>
              </select>
            </div>
          </div>
          
          <div className="filter-row">
            <div className="filter-group">
              <label className="filter-label">From Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.date_from}
                onChange={(e) => handleFilterChange({ date_from: e.target.value })}
              />
            </div>
            
            <div className="filter-group">
              <label className="filter-label">To Date</label>
              <input
                type="date"
                className="filter-input"
                value={filters.date_to}
                onChange={(e) => handleFilterChange({ date_to: e.target.value })}
              />
            </div>
            
            <div className="filter-group filter-actions">
              <div className="filter-buttons">
                <button
                  className="btn btn-primary btn-sm"
                  onClick={() => {
                    const today = new Date();
                    const weekAgo = new Date(today.getTime() - 7 * 24 * 60 * 60 * 1000);
                    handleFilterChange({
                      date_from: weekAgo.toISOString().split('T')[0],
                      date_to: today.toISOString().split('T')[0]
                    });
                  }}
                >
                  Last 7 Days
                </button>
                <button
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    const today = new Date();
                    const monthAgo = new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000);
                    handleFilterChange({
                      date_from: monthAgo.toISOString().split('T')[0],
                      date_to: today.toISOString().split('T')[0]
                    });
                  }}
                >
                  Last 30 Days
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Bulk Actions */}
      {selectedLogs.length > 0 && (
        <div className="bulk-actions">
          <div className="bulk-actions-content">
            <span className="selected-count">
              {selectedLogs.length} log(s) selected
            </span>
            <div className="bulk-controls">
              <button
                className="btn btn-outline"
                onClick={() => setSelectedLogs([])}
              >
                Clear
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Logs List */}
      <div className="logs-content">
        <div className="logs-table-container">
          <table className="logs-table">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectedLogs.length === logs.length && logs.length > 0}
                    onChange={(e) => handleSelectAll(e.target.checked)}
                  />
                </th>
                <th>Type</th>
                <th>Status</th>
                <th>Started</th>
                <th>Completed</th>
                <th>Duration</th>
                <th>Members</th>
                <th>Success Rate</th>
                <th>Triggered By</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {logs.map(log => (
                <tr key={log.id} className="log-row">
                  <td>
                    <input
                      type="checkbox"
                      checked={selectedLogs.includes(log.id)}
                      onChange={(e) => handleLogSelect(log.id, e.target.checked)}
                    />
                  </td>
                  <td>
                    <span className="sync-type-badge">
                      {log.sync_type_display || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <span className={`status-badge ${log.status || log.sync_status}`}>
                      {log.status_display || log.status || 'Unknown'}
                    </span>
                  </td>
                  <td>
                    <div className="datetime-info">
                      {log.started_at ? 
                        new Date(log.started_at).toLocaleString() : 
                        'Unknown'
                      }
                    </div>
                  </td>
                  <td>
                    <div className="datetime-info">
                      {log.completed_at ? 
                        new Date(log.completed_at).toLocaleString() : 
                        'Not completed'
                      }
                    </div>
                  </td>
                  <td>
                    <span className="duration-info">
                      {log.duration_display || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <div className="members-info">
                      <div className="members-count">
                        {log.synced_members || 0}/{log.total_members || 0}
                      </div>
                      {log.failed_members > 0 && (
                        <div className="failed-count">
                          ({log.failed_members} failed)
                        </div>
                      )}
                    </div>
                  </td>
                  <td>
                    <span className="success-rate">
                      {log.success_rate_display || 'N/A'}
                    </span>
                  </td>
                  <td>
                    <span className="triggered-by">
                      {log.triggered_by_email || 'System'}
                    </span>
                  </td>
                  <td>
                    <div className="notes-info">
                      {log.notes ? (
                        <span className="notes-text" title={log.notes}>
                          {log.notes.length > 50 ? 
                            `${log.notes.substring(0, 50)}...` : 
                            log.notes
                          }
                        </span>
                      ) : (
                        <span className="no-notes">-</span>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Empty State */}
      {logs.length === 0 && (
        <div className="empty-state">
          <div className="empty-icon">📋</div>
          <h3>No Sync Logs</h3>
          <p>No synchronization logs found for the selected criteria.</p>
        </div>
      )}
    </div>
  );
};

export default GoogleSheetsSyncLogs;
